import { c as _c } from "react-compiler-runtime";
import { createContext, useCallback, useContext, useState, useSyncExternalStore } from 'react';
import emptyFunction from 'react-utils/emptyFunction';

/**
 * Note that these sets are only ever added to, never removed from.
 * In testing, we found that there's no meaningful performance hit if we never un-render cells.
 */
import { jsx as _jsx } from "react/jsx-runtime";
/**
 * 11 is the legacy value.
 *
 * We hope to set this to 25 in the future.
 * Amplitude shows that the majority of users use page size 25.
 * For them, always showing the first 25 rows would feel snappier.
 * 25 also is generally enough to fill the screen,
 * so larger page sizes should also feel perceived performance improvements.
 */
export const ALWAYS_VISIBLE_ROW_COUNT = 11;

/**
 * 11 is the legacy value.
 *
 * We hope to se this to `Math.ceil(3440 / DEFAULT_COLUMN_WIDTH)`;
 * According to Amplitude, 95% of users have a screen width of 3440px or less.
 * Dividing by the default column width roughly gives us the number of columns that can fit on the screen.
 * Always rendering these should help improve perceived performance.
 */
const ALWAYS_VISIBLE_COLUMN_COUNT = 11;

/**
 * By putting this in a getter, we ensure new objects are being made for each call
 */
const getLocalMutationsStoreDefaultState = () => ({
  visibleRowIndexes: new Set(Array.from({
    length: ALWAYS_VISIBLE_ROW_COUNT
  }, (_, i) => i)),
  visibleColumnIndexes: new Set(Array.from({
    length: ALWAYS_VISIBLE_COLUMN_COUNT
  }, (_, i) => i))
});
const createVirtualizationStore = () => {
  /** We're able to subscribe to specific rows, so the key here is the row index ... */
  const rowSubscribers = new Map();

  /** ... but we can't subscribe to specific columns, so we have to listen to all all of them */
  const allColumnsSubscribers = new Set();
  let state = getLocalMutationsStoreDefaultState();
  let rafPending = false;
  const pendingNotifications = new Set();
  const scheduleNotify = subscriber => {
    pendingNotifications.add(subscriber);
    if (rafPending) return;
    rafPending = true;
    requestAnimationFrame(() => {
      rafPending = false;
      pendingNotifications.forEach(sub => sub());
      pendingNotifications.clear();
    });
  };
  const notifyRows = newRows => {
    newRows.forEach(rowIndex => {
      const subscribers = rowSubscribers.get(rowIndex);
      if (subscribers) {
        subscribers.forEach(scheduleNotify);
      }
    });
  };
  const notifyColumns = newColumns => {
    if (newColumns.length > 0) {
      allColumnsSubscribers.forEach(scheduleNotify);
    }
  };
  return {
    getSnapshot() {
      return state;
    },
    subscribeToRow({
      rowIndex,
      subscriber
    }) {
      // If row is already visible, no need to subscribe
      // (this includes the always visible rows)
      if (state.visibleRowIndexes.has(rowIndex)) {
        return () => {};
      }
      if (!rowSubscribers.has(rowIndex)) {
        rowSubscribers.set(rowIndex, new Set());
      }
      rowSubscribers.get(rowIndex).add(subscriber);
      return () => {
        var _rowSubscribers$get;
        (_rowSubscribers$get = rowSubscribers.get(rowIndex)) === null || _rowSubscribers$get === void 0 || _rowSubscribers$get.delete(subscriber);
      };
    },
    subscribeToAllColumns(subscriber) {
      allColumnsSubscribers.add(subscriber);
      return () => {
        allColumnsSubscribers.delete(subscriber);
      };
    },
    reset() {
      const rowIndexes = Array.from(state.visibleRowIndexes);
      const columnIndexes = Array.from(state.visibleColumnIndexes);
      state = getLocalMutationsStoreDefaultState();
      notifyRows(rowIndexes);
      notifyColumns(columnIndexes);
    },
    updateVisibleIndexes: ({
      rows,
      columns
    }) => {
      const newRows = Array.from(rows).filter(rowIndex => !state.visibleRowIndexes.has(rowIndex));
      if (newRows.length > 0) {
        state.visibleRowIndexes = new Set([...state.visibleRowIndexes, ...newRows]);
        notifyRows(newRows);
      }
      const newColumns = Array.from(columns).filter(columnIndex => !state.visibleColumnIndexes.has(columnIndex));
      if (newColumns.length > 0) {
        state.visibleColumnIndexes = new Set([...state.visibleColumnIndexes, ...columns]);
        notifyColumns(newColumns);
      }
    }
  };
};
const VirtualizationContext = /*#__PURE__*/createContext(null);
export const FullVirtualizationContext = /*#__PURE__*/createContext(false);
export const ProvideVirtualization = t0 => {
  const $ = _c(6);
  const {
    children,
    enableFullVirtualization: t1
  } = t0;
  const enableFullVirtualization = t1 === undefined ? false : t1;
  const [store] = useState(_temp);
  let t2;
  if ($[0] !== children || $[1] !== store) {
    t2 = /*#__PURE__*/_jsx(VirtualizationContext.Provider, {
      value: store,
      children: children
    });
    $[0] = children;
    $[1] = store;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== enableFullVirtualization || $[4] !== t2) {
    t3 = /*#__PURE__*/_jsx(FullVirtualizationContext.Provider, {
      value: enableFullVirtualization,
      children: t2
    });
    $[3] = enableFullVirtualization;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
};
const useVirtualizationStore = () => {
  const store = useContext(VirtualizationContext);
  if (store === null) {
    throw new Error("useVirtualizationStore must be used within a VirtualizationProvider");
  }
  return store;
};
export const useIsRowVisible = rowIndex => {
  const $ = _c(8);
  const store = useVirtualizationStore();
  const enableFullVirtualization = useContext(FullVirtualizationContext);
  let t0;
  if ($[0] !== enableFullVirtualization || $[1] !== rowIndex || $[2] !== store) {
    t0 = onStoreChange => {
      if (enableFullVirtualization) {
        return emptyFunction;
      }
      return store.subscribeToRow({
        rowIndex,
        subscriber: onStoreChange
      });
    };
    $[0] = enableFullVirtualization;
    $[1] = rowIndex;
    $[2] = store;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const subscribe = t0;
  let t1;
  if ($[4] !== enableFullVirtualization || $[5] !== rowIndex || $[6] !== store) {
    t1 = () => {
      if (enableFullVirtualization) {
        return true;
      }
      return store.getSnapshot().visibleRowIndexes.has(rowIndex);
    };
    $[4] = enableFullVirtualization;
    $[5] = rowIndex;
    $[6] = store;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  const getSnapshot = t1;
  return useSyncExternalStore(subscribe, getSnapshot);
};
const ALWAYS_VISIBLE_COLUMNS = new Set();
export const useVisibleColumnsIfRowVisible = isRowVisible => {
  const $ = _c(7);
  const store = useVirtualizationStore();
  const enableFullVirtualization = useContext(FullVirtualizationContext);
  let t0;
  if ($[0] !== enableFullVirtualization || $[1] !== isRowVisible || $[2] !== store) {
    t0 = onStoreChange => {
      if (enableFullVirtualization || !isRowVisible) {
        return emptyFunction;
      }
      return store.subscribeToAllColumns(onStoreChange);
    };
    $[0] = enableFullVirtualization;
    $[1] = isRowVisible;
    $[2] = store;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  let t1;
  if ($[4] !== enableFullVirtualization || $[5] !== store) {
    t1 = () => {
      if (enableFullVirtualization) {
        return ALWAYS_VISIBLE_COLUMNS;
      }
      return store.getSnapshot().visibleColumnIndexes;
    };
    $[4] = enableFullVirtualization;
    $[5] = store;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  return useSyncExternalStore(t0, t1);
};
export const useUpdateVisibleIndexes = () => {
  const store = useVirtualizationStore();
  return store.updateVisibleIndexes;
};
export const useResetVisibleIndexes = () => {
  const store = useVirtualizationStore();
  return store.reset;
};
function _temp() {
  return createVirtualizationStore();
}